import React, { useEffect, useContext } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import emailjs from '@emailjs/browser';
import { useLocation } from 'react-router-dom';
import { CartContext } from '../context/CartContext';

const phoneRegex = /^\+?[1-9]\d{7,14}$/;

const schema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  phone: z
    .string()
    .min(8, 'Phone must be at least 8 digits')
    .max(15, 'Phone must be at most 15 digits')
    .regex(phoneRegex, 'Enter a valid phone number, e.g. +919876543210'),
  message: z.string().min(5, 'Message must be at least 5 characters'),
  team: z.enum(['sales', 'support'])
});

export default function ContactUs() {
  const location = useLocation();
  const { clearCart } = useContext(CartContext);
  const prefillMessage = location?.state?.prefillMessage || '';
  const fromCart = Boolean(location?.state?.fromCart);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { name: '', email: '', phone: '', message: prefillMessage, team: 'sales' }
  });

  useEffect(() => {
    if (prefillMessage) setValue('message', prefillMessage);
  }, [prefillMessage, setValue]);

  async function onSubmit(data) {
    // 1. Load Environment Variables (Using process.env for Create React App)
    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const adminTemplateId = process.env.REACT_APP_EMAILJS_ADMIN_TEMPLATE_ID; // Email to YOU
    const userTemplateId = process.env.REACT_APP_EMAILJS_USER_TEMPLATE_ID;   // Email to CUSTOMER
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    try {
      // ── Email 1: Notification to Company (Sales/Support) ──
      const adminEmailPromise = emailjs.send(
        serviceId,
        adminTemplateId,
        {
          to_email: data.team === 'sales' ? 'orderdesk@aromoras.com' : 'support@aromoras.com',
          subject: `Contact request to ${data.team}`,
          from_name: data.name,
          from_email: data.email,
          phone: data.phone,
          message: `Name: ${data.name}\nEmail: ${data.email}\nPhone: ${data.phone}\n\nMessage:\n${data.message}`
        },
        publicKey
      );

      // ── Email 2: Auto-Reply to Customer ──
      // Ensure your EmailJS template uses {{to_name}} and {{to_email}} variables
      const userEmailPromise = emailjs.send(
        serviceId,
        userTemplateId,
        {
          to_name: data.name,
          to_email: data.email, // This sends the email to the customer
          team_name: data.team === 'sales' ? 'Sales Team' : 'Support Team',
          message_copy: data.message
        },
        publicKey
      );

      // Wait for both emails to send
      await Promise.all([adminEmailPromise, userEmailPromise]);

      if (fromCart && typeof clearCart === 'function') {
        clearCart();
      }
      
      window.alert('Message sent successfully! Please check your inbox for a confirmation.');
      reset();

    } catch (err) {
      console.error('Email Error:', err);
      window.alert('Failed to send message. Please try again later.');
    }
  }

  return (
    <div className="container app-content" style={{ paddingTop: 12 }}>
      <h2 className="page-title">Contact Us</h2>
      <p className="page-sub">Choose a team to connect with and send your message.</p>

      <form onSubmit={handleSubmit(onSubmit)} style={{ maxWidth: 720, display: 'grid', gap: 12 }}>
        {/* Name Field */}
        <div>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>
            Your name <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <input
            placeholder="Your name"
            {...register('name')}
            className="input"
            aria-invalid={errors.name ? 'true' : 'false'}
          />
          {errors.name && <div style={{ color: '#dc2626', marginTop: 6 }}>{errors.name.message}</div>}
        </div>

        {/* Email Field */}
        <div>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>
            Your email <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <input
            type="email"
            placeholder="Your email"
            {...register('email')}
            className="input"
            aria-invalid={errors.email ? 'true' : 'false'}
          />
          {errors.email && <div style={{ color: '#dc2626', marginTop: 6 }}>{errors.email.message}</div>}
        </div>

        {/* Phone Field */}
        <div>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>
            Cell number <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <input
            type="tel"
            placeholder="Cell number (required) e.g. +919876543210"
            {...register('phone')}
            className="input"
            aria-invalid={errors.phone ? 'true' : 'false'}
          />
          {errors.phone && <div style={{ color: '#dc2626', marginTop: 6 }}>{errors.phone.message}</div>}
        </div>

        {/* Team Selector */}
        <div>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>
            Team
          </label>
          <select {...register('team')} className="input" aria-invalid={errors.team ? 'true' : 'false'}>
            <option value="sales">Sales Team</option>
            <option value="support">Customer Support</option>
          </select>
          {errors.team && <div style={{ color: '#dc2626', marginTop: 6 }}>{errors.team.message}</div>}
        </div>

        {/* Message Field */}
        <div>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>
            Message <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <textarea
            placeholder="Message"
            {...register('message')}
            rows="6"
            className="input"
            aria-invalid={errors.message ? 'true' : 'false'}
          />
          {errors.message && <div style={{ color: '#dc2626', marginTop: 6 }}>{errors.message.message}</div>}
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 12 }}>
          <button type="submit" className="btn" disabled={isSubmitting} aria-disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Send'}
          </button>
          <button
            type="button"
            onClick={() => reset()}
            className="btn secondary"
            disabled={isSubmitting}
          >
            Reset
          </button>
        </div>
      </form>
    </div>
  );
}